import { makeStyles } from '@material-ui/core/styles';
import {
  BackgroundColors,
  ColorNames,
  TextColors,
  SurfaceColors,
  TextOpacities,
} from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      borderRadius: '4px',
      backgroundColor: ColorNames.white,

      '&.MuiTable-root': {
        backgroundColor: SurfaceColors.dark,
      },
    },
    section: {
      ...muiTheme.typography.body2,
      padding: muiTheme.spacing(0, 8, 2),

      [muiTheme.breakpoints.down('sm')]: {
        padding: muiTheme.spacing(0, 4, 2),
      },

      '@media print': {
        padding: muiTheme.spacing(0, 4, 2),
      },
    },
    headerRow: {
      '&.MuiTableRow-head': {
        backgroundColor: BackgroundColors.dark,
      },
    },
    headerCell: {
      '&.MuiTableCell-head': {
        ...muiTheme.typography.body2,
        padding: muiTheme.spacing(1.5),
        minWidth: 0,
        lineHeight: 'normal',
        color: TextColors.light,

        [muiTheme.breakpoints.up('md')]: {
          padding: muiTheme.spacing(1.5),
        },
      },

      '&.MuiTableCell-head small': {
        whiteSpace: 'nowrap',
      },

      '&.MuiTableCell-head:first-child': {
        paddingLeft: muiTheme.spacing(2),

        [muiTheme.breakpoints.up('md')]: {
          paddingLeft: muiTheme.spacing(3),
        },
      },

      '&.MuiTableCell-head:last-child': {
        paddingRight: muiTheme.spacing(2),

        [muiTheme.breakpoints.up('md')]: {
          paddingRight: muiTheme.spacing(3),
        },
      },
    },
    bodyRow: {
      '&:not(:nth-child(even))': {
        backgroundColor: hexToRGBA(BackgroundColors.light, 0.6),
      },
    },
    bodyCell: {
      '&.MuiTableCell-body': {
        verticalAlign: 'baseline',
        padding: muiTheme.spacing(1.5),
        ...muiTheme.typography.body2,
        lineHeight: 1.25,
        border: 0,
        minWidth: '150px',

        [muiTheme.breakpoints.up('md')]: {
          minWidth: '200px',
        },
      },

      '&.MuiTableCell-body:first-child': {
        paddingLeft: muiTheme.spacing(3),
      },

      '&.MuiTableCell-body:last-child': {
        paddingRight: muiTheme.spacing(3),
      },
    },
    bodyCellList: {
      marginBlockStart: 0,
      paddingInlineStart: muiTheme.spacing(3),
    },
    spinnerCell: {
      padding: muiTheme.spacing(6),
    },
    table: {
      '&.MuiTable-root': {
        ...muiTheme.typography.body2,

        '@media print': {
          margin: 0,
        },
      },
    },
    wrapper: {
      width: '100%',

      '&': {
        [muiTheme.breakpoints.down('sm')]: {
          width: `calc(100% + ${muiTheme.spacing(8)}px)`,
          marginLeft: muiTheme.spacing(-4),
          marginTop: muiTheme.spacing(2),
          overflow: 'hidden',
        },

        '& button:hover': {
          cursor: 'pointer',
        },
        '& .close-modal.MuiButtonBase-root': {
          float: 'right',
          border: 'none',
          backgroundColor: 'inherit',
          paddingTop: muiTheme.spacing(2),
          opacity: '0.7',
          minWidth: 'auto',
          '&:hover': {
            cursor: 'Pointer',
            opacity: '1',
            backgroundColor: 'inherit',
          },
          '@media print': {
            display: 'none',
          },
        },
        '& .sdl-detail-info-heading': {
          display: 'flex',
          justifyContent: 'space-between',
          padding: muiTheme.spacing(5, 8, 3),
          [muiTheme.breakpoints.down('sm')]: {
            padding: muiTheme.spacing(5, 4, 3),
          },
          '@media print': {
            padding: muiTheme.spacing(0, 4, 3),
          },
        },
        '& .basic-info button': {
          '@media print': {
            display: 'none',
          },
        },
        '& .basic-detail-heading': {
          display: 'flex',
          justifyContent: 'space-between',
          '& h5.MuiTypography-root': {
            marginTop: 0,
            marginBottom: '10px',
          },
          '& Button': {
            minWidth: 'unset',
          },
        },
        '& .basic-detail-data, .basic-additional-data': {
          padding: muiTheme.spacing(1.5, 0),
          display: 'block',
          columnCount: 3,
          columnGap: muiTheme.spacing(1),
          [muiTheme.breakpoints.down('xs')]: {
            columnCount: 1,
          },
          '@media print': {
            columnCount: 2,
            margin: '0 10%',
          },
          '& > div': {
            breakInside: 'avoid',
            '& h6.MuiTypography-root': {
              marginBottom: '4px',
              wordBreak: 'break-word',
            },
          },
        },
        '& .basic-additional-data': {
          columnCount: 4,
        },
        '& .basic-info, .additional-info': {
          borderBottom: `solid 1px ${hexToRGBA(SurfaceColors.dark, TextOpacities.dark3)}`,
          '& ul': {
            listStyle: 'none',
            paddingLeft: '0',
            marginBottom: muiTheme.spacing(1.5),
            marginTop: '0',
            opacity: '.7',
          },
          '@media print': {
            textAlign: 'left',
          },
        },
        '& .additional-detail-heading, .firmware-detail-heading': {
          display: 'flex',
          justifyContent: 'space-between',
          '& h5.MuiTypography-root': {
            marginTop: 0,
            marginBottom: '10px',
          },
        },
        '& .basic-firmware-data': {
          padding: muiTheme.spacing(1.5, 0),
        },
        '& .additional-info': {
          pageBreakInside: 'avoid',
        },
        '& .firmware-info': {
          marginBottom: muiTheme.spacing(6),
          pageBreakInside: 'avoid',
        },
      },
    },
  }),
  { name: 'GenSDLDetail' },
);

export default useStyles;
